<template>
	<div>
		<div v-if="documentsArray.length !== 0">
			<h3 class="text-center bold" style="margin-bottom: 30px">Document Submission</h3>
			<h4 class="text-center" style="margin-bottom: 30px">Document Instructions</h4>
			<div class="text-center" style="margin-bottom: 40px">
				<div v-html="formattedText()"></div>
			</div>

			<div>
				<md-table v-if="documentsArray && documentsArray.length > 0" v-model="documentsArray">
					<md-table-row slot="md-table-row" slot-scope="{ item }">
						<md-table-cell md-label="Document" md-sort-by="documentName">{{ item.documentName }}</md-table-cell>

						<md-table-cell md-label="Verification Status" md-sort-by="isVerified">
							{{ item.isVerified ? 'Verified' : 'Not Verified' }}
						</md-table-cell>

						<md-table-cell md-label="Resubmit Required" md-sort-by="shouldResubmit">
							{{ item.shouldResubmit ? 'Yes' : 'No' }}
						</md-table-cell>

						<md-table-cell>
							<md-button v-if="item.url" class="md-success" style="width: 100px" @click="previewDocument(item)"> View </md-button>
						</md-table-cell>

						<md-table-cell>
							<div>
								<input type="file" ref="fileInput" @change="handleFileChange" accept="application/pdf" style="display: none" />
								<md-button :id="'upload-btn-' + item.id" @click="showFileDialog(item)" style="width: 100px" class="md-info button">
									<span>{{ item.shouldResubmit ? 'Resubmit' : 'Submit' }}</span>
								</md-button>
							</div>
						</md-table-cell>

						<md-table-cell>
							<span :style="{ color: item.isVerified && !item.shouldResubmit ? 'green' : 'red', fontSize: '1rem' }">
								{{ item.isVerified && !item.shouldResubmit ? '✅' : '❌' }}
							</span>
						</md-table-cell>
					</md-table-row>
				</md-table>

				<div v-else>No Documents Requested.</div>
			</div>

			<md-dialog :md-active.sync="showUploadProgress" style="min-width: 70% !important">
				<md-dialog-title v-if="!uploadSuccess"> Uploading {{ currentDocument ? currentDocument.documentName : 'Document' }}</md-dialog-title>
				<md-dialog-title v-else>Success! 🥳 Upload Successful!</md-dialog-title>
				<md-dialog-content>
					<div v-if="!uploadSuccess">
						<md-progress-bar :md-value="uploadPercentage" :md-buffer="100"></md-progress-bar>
						<p>{{ uploadPercentage }}%</p>
					</div>
					<div v-else>
						<p>The document "{{ currentDocument ? currentDocument.documentName : 'Document' }}" was uploaded successfully!</p>
					</div>
				</md-dialog-content>
				<md-dialog-actions>
					<md-button class="md-primary" @click="closeUploadModal" :disabled="!uploadSuccess">Close</md-button>
				</md-dialog-actions>
			</md-dialog>
		</div>
		<div v-else>
			<h3 class="text-center bold" style="margin-top: 50px">There are no documents to submit</h3>
		</div>
	</div>
</template>

<script>
import firebase from 'firebase/compat/app';
import db from '@/firebase/init';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/storage';
import { uploadBytesResumable, getDownloadURL } from 'firebase/storage';

export default {
	data() {
		return {
			currentDocument: null,
			loading: false,
			previewingUrl: null,
			submittedDocuments: [],
			showUploadProgress: false,
			uploadPercentage: 0,
			uploadSuccess: false,
			documentSubmissionInstructions: '',
		};
	},
	props: {
		job: { type: Object },
		micro: { type: Object },
		application: { type: Object },
		user: { type: Object },
	},
	computed: {
		documentsArray() {
			const result = [];
			if (!this.application) return result;

			if (this.application.documentsRequested) {
				const requestedDocs = Object.entries(this.application.documentsRequested)
					.filter(([_, docData]) => docData.isRequired === true)
					.map(([name, docData], index) => this.processDocument(name, docData, index));

				result.push(...requestedDocs);
			}

			if (this.application.additionalDocuments?.length) {
				const additionalDocs = this.application.additionalDocuments.map((doc, index) => this.processDocument(doc.name, doc, index));

				result.push(...additionalDocs);
			}

			return result;
		},
	},
	methods: {
		formattedText() {
			let text = this.application.documentSubmissionInstructions.replace(/^"|"$/g, '');
			text = text.replace(/<(?!b|u|strong|em|i|\/b|\/u|\/strong|\/em|\/i)[^>]*>/g, '');
			return text;
		},
		closeUploadModal() {
			this.showUploadProgress = false;
		},
		showFileDialog(document) {
			this.currentDocument = document;
			this.$refs.fileInput.click();
		},
		handleFileChange(event) {
			const file = event.target.files[0];
			if (!file || !this.currentDocument) {
				return;
			}
			const validationError = this.validateFile(file);
			if (validationError) {
				this.showNotification(validationError);
				return;
			}
			this.uploadFile(file, this.currentDocument);
			event.target.value = '';
		},
		validateFile(file) {
			if (!file) {
				return 'No file selected.';
			}
			if (file.type !== 'application/pdf') {
				return 'Please upload a PDF file.';
			}
			if (file.size > 2 * 1024 * 1024) {
				return 'File size exceeds 2MB limit.';
			}
			return null;
		},
		async uploadFile(file, document) {
			this.loading = true;
			this.uploadPercentage = 0;
			this.uploadSuccess = false;
			this.showUploadProgress = true;

			try {
				let docType = document.documentName;

				const timestamp = new Date().getTime();
				const fileName = `${timestamp}_${file.name}`;

				const storageRef = firebase.storage().ref().child(`users/students/${this.user.alias}/application-documents/${this.job.jobId}/${document.originalName}/${fileName}`);

				const metadata = {
					contentType: 'application/pdf',
					customMetadata: { userAlias: this.user.alias, applicationId: this.application.id, docType, certified: document.shouldCertify },
				};

				const uploadTask = uploadBytesResumable(storageRef, file, metadata);

				uploadTask.on('state_changed', snapshot => {
					const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					this.uploadPercentage = Math.round(progress);
				});

				await uploadTask;
				const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);

				this.documentsArray[this.documentsArray.findIndex(item => item && item.id === document.id)].url = downloadUrl;
				this.updateSubmittedDocuments(db, this.application, document, downloadUrl, this.job);
				this.uploadSuccess = true;
			} catch (error) {
				console.error(`Error uploading ${document.documentName}: ${error.message}`);
			} finally {
				this.loading = false;
			}
		},
		async updateSubmittedDocuments(db, application, document, downloadUrl, job) {
			try {
				const applicationRef = doc(db, 'applications', application.id);
				const applicationSnapshot = await getDoc(applicationRef);

				if (applicationSnapshot.exists()) {
					await updateDoc(applicationRef, {
						[`submittedDocuments.${document.originalName}`]: {
							isVerified: false,
							shouldResubmit: false,
							shouldCertify: document.shouldCertify,
							url: downloadUrl,
						},
					});
				} else {
					await setDoc(applicationRef, {
						submittedDocuments: {
							[document.originalName]: {
								isVerified: false,
								shouldResubmit: false,
								shouldCertify: document.shouldCertify,
								url: downloadUrl,
							},
						},
					});
				}
			} catch (error) {
				console.error('Error updating/creating document:', error);
			}
		},
		previewDocument(document) {
			const url = document.url;
			if (url) {
				window.open(url, '_blank');
			} else {
				console.error('No URL found for this document:', document.documentName);
			}
		},
		downloadDocument() {
			if (this.previewingUrl) {
				window.open(this.previewingUrl, '_blank');
			}
		},
		processDocument(name, docData, index) {
			const submittedDocs = this.application.submittedDocuments || {};
			const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
			const formattedName = capitalizedName.replace(/([A-Z])/g, ' $1').trim();
			const displayName = docData.shouldCertify ? `${formattedName} (certified copy)` : formattedName;
			const uniqueId = `${index}-${name}`;
			const submittedDocData = submittedDocs[formattedName];

			return {
				id: uniqueId,
				documentName: displayName,
				originalName: formattedName,
				isRequired: docData.isRequired !== undefined ? docData.isRequired : true,
				shouldCertify: docData.shouldCertify || false,
				isVerified: submittedDocData?.isVerified || false,
				shouldResubmit: submittedDocData?.shouldResubmit || false,
				url: submittedDocData?.url || null,
			};
		},
	},
};
</script>

<style scoped>
.md-button {
	position: relative;
}
</style>
